import { CartAddEvent, CartErrorEvent } from '../assets/events.js';
import { formatMoney } from '../assets/money-formatting.js';

class BundleBuilderComponent extends HTMLElement {
  #bundleTokenAlphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  #mobileQuery = null;
  #renderScheduled = false;
  #cachedCardsByPane = null;
  #cachedSellingPlans = new Map();
  #preloadedImages = new Map();

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.config = this.#readConfig();
    if (!this.config) return;

    const firstTab = this.querySelector('[data-tab-button]');

    this.state = {
      activeTab: firstTab?.dataset.tabId ?? '',
      activeFilters: [],
      tierKey: this.#getDefaultTierKey(),
      purchaseMode: 'one-time',
      frequency: '',
      selectedItems: [],
      mobileSummaryExpanded: false,
      submitting: false,
    };

    this.#mobileQuery = window.matchMedia('(max-width: 749px)');

    this.refs = {
      panes: [...this.querySelectorAll('[data-pane]')],
      tabButtons: [...this.querySelectorAll('[data-tab-button]')],
      tierButtons: [...this.querySelectorAll('[data-tier-button]')],
      purchaseButtons: [...this.querySelectorAll('[data-purchase-button]')],
      filterToggles: [...this.querySelectorAll('[data-filter-toggle]')],
      frequencyGroup: this.querySelector('[data-frequency-group]'),
      oneTimeOriginal: this.querySelector('[data-one-time-original]'),
      subscribeLabel: this.querySelector('[data-subscribe-label]'),
      oneTimePrice: this.querySelector('[data-one-time-price]'),
      subscribeOriginal: this.querySelector('[data-subscribe-original]'),
      subscribePrice: this.querySelector('[data-subscribe-price]'),
      subscriptionNote: this.querySelector('[data-subscription-note]'),
      slots: [...this.querySelectorAll('[data-slot]')],
      tierCounts: [...this.querySelectorAll('[data-tier-count]')],
      subtotal: this.querySelector('[data-subtotal]'),
      savings: this.querySelector('[data-savings]'),
      total: this.querySelector('[data-total]'),
      originalTotal: this.querySelector('[data-original-total]'),
      status: this.querySelector('[data-status]'),
      error: this.querySelector('[data-error]'),
      submit: this.querySelector('[data-submit-button]'),
      submitLabel: this.querySelector('[data-submit-label]'),
      submitCompare: this.querySelector('[data-submit-compare]'),
      emptyState: this.querySelector('[data-empty-state]'),
      summaryBody: this.querySelector('[data-mobile-summary-body]'),
      mobileToggle: this.querySelector('[data-mobile-summary-toggle]'),
      mobileToggleLabel: this.querySelector('[data-mobile-summary-label]'),
    };

    this.#buildCardCache();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    this.#scheduleRender();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.#cachedSellingPlans.clear();
    this.#preloadedImages.clear();
    this.#cachedCardsByPane = null;
  }

  addProductFromDrawer(productId) {
    const card = this.getDrawerProductCard(productId);
    if (!(card instanceof HTMLElement)) return false;

    const addButton = card.querySelector('[data-product-toggle]');
    if (!(addButton instanceof HTMLButtonElement) || addButton.disabled || addButton.hidden) return false;

    const beforeCount = this.state.selectedItems.length;
    addButton.click();
    return this.state.selectedItems.length > beforeCount;
  }

  getDrawerProductCard(productId) {
    if (!productId) return null;
    const cards = [...this.querySelectorAll('[data-product-card]')]
      .filter((item) => item.dataset.productId === String(productId));
    return cards.find((item) => !item.hidden && !item.closest('[data-pane]')?.hidden) || cards[0] || null;
  }

  clickDrawerProductQuantity(productId, action) {
    const card = this.getDrawerProductCard(productId);
    if (!(card instanceof HTMLElement)) return false;

    const selector = action === 'increment' ? '[data-qty-increment]' : '[data-qty-decrement]';
    const button = card.querySelector(selector);
    if (!(button instanceof HTMLButtonElement) || button.disabled) return false;

    button.click();
    return true;
  }

  #readConfig() {
    const node = this.querySelector('[data-bundle-builder-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return null;
    }
  }

  #buildCardCache() {
    this.#cachedCardsByPane = new Map();
    for (const pane of this.refs.panes) {
      const cards = [...pane.querySelectorAll('[data-product-card]')];
      this.#cachedCardsByPane.set(pane.dataset.tabId, cards);

      for (const card of cards) {
        const id = card.dataset.productId;
        if (id && !this.#cachedSellingPlans.has(id)) {
          this.#cachedSellingPlans.set(id, this.#parseSellingPlans(card));
        }
        const imgUrl = card.dataset.productImage;
        if (imgUrl && !this.#preloadedImages.has(imgUrl)) {
          const img = new Image();
          img.src = imgUrl;
          this.#preloadedImages.set(imgUrl, img);
        }
      }
    }
  }

  #parseSellingPlans(card) {
    const node = card.querySelector('[data-selling-plans]');
    if (!(node instanceof HTMLScriptElement)) return [];
    try {
      const plans = JSON.parse(node.textContent || '[]');
      return Array.isArray(plans) ? plans : [];
    } catch {
      return [];
    }
  }

  #scheduleRender() {
    if (this.#renderScheduled) return;
    this.#renderScheduled = true;
    requestAnimationFrame(() => {
      this.#renderScheduled = false;
      this.#render();
    });
  }

  #handleClick = async (event) => {
    const target = event.target;

    const tabButton = target.closest('[data-tab-button]');
    if (tabButton) {
      this.state.activeTab = tabButton.dataset.tabId || this.state.activeTab;
      this.#scheduleRender();
      return;
    }

    const tierButton = target.closest('[data-tier-button]');
    if (tierButton) {
      this.#setTier(tierButton.dataset.tierKey || this.state.tierKey);
      return;
    }

    const purchaseButton = target.closest('[data-purchase-button]');
    if (purchaseButton) {
      this.state.purchaseMode =
        purchaseButton.dataset.mode === 'subscribe' ? 'subscribe' : 'one-time';
      this.#scheduleRender();
      return;
    }

    const frequencyButton = target.closest('[data-frequency-button]');
    if (frequencyButton) {
      this.state.frequency = frequencyButton.dataset.frequency || '';
      this.#scheduleRender();
      return;
    }

    const qtyButton = target.closest('[data-qty-increment], [data-qty-decrement]');
    if (qtyButton) {
      const card = qtyButton.closest('[data-product-card]');
      if (!(card instanceof HTMLElement)) return;
      if (qtyButton.hasAttribute('data-qty-increment')) {
        this.#addProductUnits(card, 1);
      } else {
        this.#removeOneProductUnit(card.dataset.productId);
      }
      return;
    }

    const addButton = target.closest('[data-product-toggle]');
    if (addButton) {
      const card = addButton.closest('[data-product-card]');
      if (!(card instanceof HTMLElement)) return;
      this.#addProductUnits(card, 1);
      return;
    }

    const slotRemove = target.closest('[data-slot-remove]');
    if (slotRemove) {
      event.stopPropagation();
      const slot = slotRemove.closest('[data-slot]');
      const slotIndex = Number(slot?.dataset.slotIndex ?? -1);
      const item = this.state.selectedItems[slotIndex];
      if (item) this.#removeSelectedItem(item.key);
      return;
    }

    const submitButton = target.closest('[data-submit-button]');
    if (submitButton) {
      await this.#submitBundle();
      return;
    }

    const mobileToggle = target.closest('[data-mobile-summary-toggle]');
    if (mobileToggle) {
      this.state.mobileSummaryExpanded = !this.state.mobileSummaryExpanded;
      document.body.classList.toggle('bundle-open', this.state.mobileSummaryExpanded);
      this.#scheduleRender();
    }
  };

  #handleChange = (event) => {
    const filterToggle = event.target.closest('[data-filter-toggle]');
    if (filterToggle) {
      this.state.activeFilters = this.refs.filterToggles
        .filter((t) => t instanceof HTMLInputElement && t.checked)
        .map((t) => t.dataset.filterToggle || '')
        .filter(Boolean);
      this.#scheduleRender();
    }
  };

  #setTier(nextTierKey) {
    const nextTier = this.config.tiers?.[nextTierKey];
    if (!nextTier) return;
    if (this.state.selectedItems.length > Number(nextTier.size || 0)) return;
    this.state.tierKey = nextTierKey;
    this.#scheduleRender();
  }

  #getDefaultTierKey() {
    const tiers = this.config.tiers || {};
    const configured = String(this.config.defaultTier ?? '');
    if (tiers[configured]) return configured;
    return Object.keys(tiers)[0] ?? configured;
  }

  #getCurrentTier() {
    const tiers = this.config.tiers || {};
    const fallbackKey = Object.keys(tiers)[0];
    return (
      tiers[this.state.tierKey] ??
      (fallbackKey ? tiers[fallbackKey] : null) ?? { size: 0, discountPercent: 0 }
    );
  }

  #getNextTierKey(requiredSize) {
    const tiers = this.config.tiers || {};
    let best = null;
    for (const key in tiers) {
      const size = Number(tiers[key].size || 0);
      if (size >= requiredSize && (best === null || size < Number(tiers[best].size))) {
        best = key;
      }
    }
    return best;
  }

  #syncTierToSelectionCount() {
    const count = this.state.selectedItems.length;
    const nextKey = this.#getNextTierKey(Math.max(count, 1));
    if (nextKey) this.state.tierKey = nextKey;
  }

  #getRemainingSlots() {
    return Math.max(this.#getCurrentTier().size - this.state.selectedItems.length, 0);
  }

  #canAddOneMore() {
    if (this.#getRemainingSlots() > 0) return true;
    return this.#getNextTierKey(this.state.selectedItems.length + 1) != null;
  }

  #countSelectedUnits(productId) {
    let count = 0;
    for (const item of this.state.selectedItems) {
      if (item.productId === productId) count++;
    }
    return count;
  }

  #getSelectedSubtotal() {
    let sum = 0;
    for (const item of this.state.selectedItems) sum += item.price;
    return sum;
  }
 
  #getAdjustedPrice(basePrice, plan) {
    const type = plan?.adjustmentType;
    const value = Number(plan?.adjustmentValue || 0);
    if (type === 'percentage') return Math.max(basePrice - Math.round((basePrice * value) / 100), 0);
    if (type === 'fixed_amount') return Math.max(basePrice - value, 0);
    if (type === 'price') return Math.max(value, 0);
    return basePrice;
  }

  #formatSubscribeLabel(discountLabel) {
    const template = this.config.strings.subscribeLabelTemplate || 'Subscribe & Save {sub_discount}';
    const resolved = discountLabel || this.config.strings.subscriptionDiscountFallback || '';
    return template
      .replaceAll('{sub_discount}', resolved)
      .replaceAll('{sub_disocount}', resolved);
  }

  #getSubscriptionDiscountLabel(plan, subtotal) {
    if (!plan) return this.config.strings.subscriptionDiscountFallback || '';

    const type = plan.itemPlans?.[0]?.adjustmentType || plan.adjustmentType;
    const value = Number(plan.itemPlans?.[0]?.adjustmentValue ?? plan.adjustmentValue ?? 0);

    if (type === 'percentage' && value > 0) return `${value}%`;
    if (subtotal > 0 && plan.savings > 0) return `${Math.round((plan.savings / subtotal) * 100)}%`;
    return this.config.strings.subscriptionDiscountFallback || '';
  }

  #getCommonSellingPlans() {
    const items = this.state.selectedItems;
    if (items.length === 0) return [];
    if (items.some((item) => item.sellingPlans.length === 0)) return [];

    const results = [];
    const seen = new Set();
    const firstPlans = items[0].sellingPlans;

    for (const firstPlan of firstPlans) {
      if (seen.has(firstPlan.name)) continue;

      const matched = new Array(items.length);
      matched[0] = firstPlan;
      let allMatch = true;

      for (let i = 1; i < items.length; i++) {
        const found = items[i].sellingPlans.find((p) => p.name === firstPlan.name);
        if (!found) { allMatch = false; break; }
        matched[i] = found;
      }

      if (!allMatch) continue;
      seen.add(firstPlan.name);

      let total = 0;
      for (let i = 0; i < items.length; i++) {
        total += this.#getAdjustedPrice(items[i].price, matched[i]);
      }
      const subtotal = this.#getSelectedSubtotal();

      results.push({
        key: firstPlan.name,
        label: firstPlan.name,
        itemPlans: matched,
        total,
        savings: Math.max(subtotal - total, 0),
      });
    }

    return results;
  }

  #getActiveSellingPlanOption(commonPlans) {
    if (commonPlans.length === 0) return null;
    const active = commonPlans.find((p) => p.key === this.state.frequency) ?? commonPlans[0];
    if (active && this.state.frequency !== active.key) this.state.frequency = active.key;
    return active;
  }

  #getPricing(commonPlans) {
    const subtotal = this.#getSelectedSubtotal();
    const tier = this.#getCurrentTier();
    const tierSavings = Math.round((subtotal * Number(tier.discountPercent || 0)) / 100);
    const subPlan = this.#getActiveSellingPlanOption(commonPlans);
    const isSubscribe = this.state.purchaseMode === 'subscribe';
    const subSavings = isSubscribe && subPlan ? subPlan.savings : 0;
    const oneTimeTotal = Math.max(subtotal - tierSavings, 0);
    const subTotal = subPlan ? Math.max(subPlan.total - tierSavings, 0) : null;

    return {
      subtotal,
      savings: tierSavings + subSavings,
      total: Math.max(subtotal - tierSavings - subSavings, 0),
      oneTimeTotal,
      subscriptionTotal: subTotal,
    };
  }

  #getActiveCards() {
    return this.#cachedCardsByPane?.get(this.state.activeTab) ?? [];
  }

  #addProductUnits(card, quantity) {
    if (card.dataset.available !== 'true') return;

    const productId = card.dataset.productId;
    const variantId = card.dataset.variantId;
    const price = Number(card.dataset.productPrice || 0);
    if (!productId || !variantId) return;

    let unitsToAdd = Math.max(1, quantity);
    const neededSize = this.state.selectedItems.length + unitsToAdd;
    const currentTier = this.#getCurrentTier();

    if (neededSize > currentTier.size) {
      const nextKey = this.#getNextTierKey(neededSize);
      if (!nextKey) {
        unitsToAdd = this.#getRemainingSlots();
      } else {
        this.state.tierKey = nextKey;
      }
    }

    const remaining = this.#getRemainingSlots();
    if (remaining <= 0) return;
    unitsToAdd = Math.min(unitsToAdd, remaining);
    if (unitsToAdd <= 0) return;

    const inventoryImage = card.dataset.productImage || '';
    const title = card.dataset.productTitle || '';
    const color = card.dataset.productColor || '#FFD8B4';
    const sellingPlans = this.#cachedSellingPlans.get(productId) || [];
    const ts = Date.now();
    const base = this.state.selectedItems.length;

    const next = [...this.state.selectedItems];
    for (let i = 0; i < unitsToAdd; i++) {
      next.push({
        key: `${productId}-${ts}-${i}-${base + i}`,
        productId,
        variantId,
        price,
        title,
        inventoryImage,
        color,
        sellingPlans,
      });
    }

    this.state.selectedItems = next;
    this.#clearError();
    this.#scheduleRender();
  }

  #removeOneProductUnit(productId) {
    if (!productId) return;
    const items = this.state.selectedItems;
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].productId === productId) {
        this.#removeSelectedItem(items[i].key);
        return;
      }
    }
  }

  #removeSelectedItem(itemKey) {
    const idx = this.state.selectedItems.findIndex((item) => item.key === itemKey);
    if (idx === -1) return;
    this.state.selectedItems = this.state.selectedItems.filter((item) => item.key !== itemKey);
    this.#syncTierToSelectionCount();
    this.#scheduleRender();
  }

  #renderTabs() {
    for (const btn of this.refs.tabButtons) {
      const active = btn.dataset.tabId === this.state.activeTab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
    }
  }

  #applyFilters() {
    let visibleCount = 0;
    const activeTabId = this.state.activeTab;
    const filters = this.state.activeFilters;
    const hasFilters = filters.length > 0;

    for (const pane of this.refs.panes) {
      const isActive = pane.dataset.tabId === activeTabId;
      pane.hidden = !isActive;
      if (!isActive) continue;

      const cards = this.#cachedCardsByPane?.get(pane.dataset.tabId) ?? [];
      for (const card of cards) {
        if (!hasFilters) {
          card.hidden = false;
          visibleCount++;
          continue;
        }
        const types = card.dataset.filterTypes || '';
        let show = true;
        for (const f of filters) {
          if (!types.includes(`|${f}|`)) { show = false; break; }
        }
        card.hidden = !show;
        if (show) visibleCount++;
      }
    }

    if (this.refs.emptyState) this.refs.emptyState.hidden = visibleCount > 0;
  }

  #renderCards() {
    const remaining = this.#getRemainingSlots();
    const canAdd = this.#canAddOneMore();
    const submitting = this.state.submitting;
    const addLabel = this.config.strings.addLabel;
    const soldOutLabel = this.config.strings.soldOutLabel || addLabel;

    const cards = this.#getActiveCards();
    for (const card of cards) {
      if (card.hidden) continue;

      const productId = card.dataset.productId;
      const available = card.dataset.available === 'true';
      const count = productId ? this.#countSelectedUnits(productId) : 0;
      const hasSelection = count > 0;

      card.classList.toggle('is-selected', hasSelection);

      const addBtn = card._addBtn ?? (card._addBtn = card.querySelector('[data-product-toggle]'));
      const btnText = card._btnText ?? (card._btnText = card.querySelector('[data-card-button-text]'));
      const qtyWrap = card._qtyWrap ?? (card._qtyWrap = card.querySelector('[data-card-quantity]'));
      const qtyCount = card._qtyCount ?? (card._qtyCount = card.querySelector('[data-qty-count]'));

      if (btnText) btnText.textContent = available ? addLabel : soldOutLabel;

      if (addBtn instanceof HTMLButtonElement) {
        addBtn.hidden = hasSelection;
        addBtn.disabled = !available || !canAdd || submitting;
      }

      if (qtyWrap) qtyWrap.hidden = !hasSelection;
      if (qtyCount) qtyCount.textContent = String(count);

      if (!card._qtyBtns) {
        card._qtyBtns = [...card.querySelectorAll('[data-qty-increment], [data-qty-decrement]')];
      }
      for (const qb of card._qtyBtns) {
        if (!(qb instanceof HTMLButtonElement)) continue;
        qb.disabled = qb.hasAttribute('data-qty-increment')
          ? !available || !canAdd || submitting
          : !available || count === 0 || submitting;
      }
    }
  }

  #renderTiers() {
    const count = this.state.selectedItems.length;
    const submitting = this.state.submitting;
    const activeKey = this.state.tierKey;

    for (const btn of this.refs.tierButtons) {
      const size = Number(this.config.tiers?.[btn.dataset.tierKey]?.size || 0);
      const active = btn.dataset.tierKey === activeKey;
      btn.classList.toggle('is-active', active);
      btn.disabled = submitting || count > size;
    }
  }

  #renderPurchaseMode(commonPlans, pricing) {
    const hasSubscription = commonPlans.length > 0;
    const showNote = this.state.selectedItems.length > 0 && !hasSubscription;

    if (!hasSubscription && this.state.purchaseMode === 'subscribe') {
      this.state.purchaseMode = 'one-time';
    }

    const showFreqs = hasSubscription && this.state.purchaseMode === 'subscribe';
    const submitting = this.state.submitting;

    for (const btn of this.refs.purchaseButtons) {
      const active = btn.dataset.mode === this.state.purchaseMode;
      const isSub = btn.dataset.mode === 'subscribe';
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
      btn.hidden = false;
      btn.disabled = submitting || (isSub && !hasSubscription);
    }

    if (this.refs.subscriptionNote) {
      this.refs.subscriptionNote.hidden = !showNote;
      if (showNote) {
        this.refs.subscriptionNote.textContent =
          this.config.strings.subscriptionUnavailableNote ||
          "Subscription is unavailable because the selected products don't have subscription plans.";
      }
    }

    if (this.refs.frequencyGroup) {
      this.refs.frequencyGroup.hidden = !showFreqs;

      if (showFreqs) {
        const activeOption = this.#getActiveSellingPlanOption(commonPlans);
        const frag = document.createDocumentFragment();
        for (const plan of commonPlans) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'bundle-builder__frequency';
          btn.dataset.frequencyButton = '';
          btn.dataset.frequency = plan.key;
          btn.textContent = plan.label;
          btn.classList.toggle('is-active', plan.key === activeOption?.key);
          frag.appendChild(btn);
        }
        this.refs.frequencyGroup.textContent = '';
        this.refs.frequencyGroup.appendChild(frag);
      } else {
        this.refs.frequencyGroup.textContent = '';
      }
    }

    if (this.refs.subscribeLabel) {
      const best = commonPlans.reduce(
        (b, p) => (!b || p.savings > b.savings ? p : b), null
      );
      this.refs.subscribeLabel.textContent = this.#formatSubscribeLabel(
        hasSubscription ? this.#getSubscriptionDiscountLabel(best, pricing.subtotal) : null
      );
    }

    if (this.refs.oneTimePrice) {
      this.refs.oneTimePrice.textContent = this.#formatMoney(pricing.oneTimeTotal);
    }
    if (this.refs.oneTimeOriginal) {
      this.refs.oneTimeOriginal.hidden = pricing.subtotal <= pricing.oneTimeTotal;
      this.refs.oneTimeOriginal.textContent = this.#formatMoney(pricing.subtotal);
    }
    if (this.refs.subscribeOriginal) {
      this.refs.subscribeOriginal.hidden = !hasSubscription;
      this.refs.subscribeOriginal.textContent = this.#formatMoney(pricing.oneTimeTotal);
    }
    if (this.refs.subscribePrice) {
      this.refs.subscribePrice.textContent = hasSubscription
        ? this.#formatMoney(pricing.subscriptionTotal ?? pricing.oneTimeTotal)
        : this.#formatMoney(pricing.oneTimeTotal);
    }
  }

  #renderSlots() {
    const tierSize = this.#getCurrentTier().size;
    const items = this.state.selectedItems;
    const submitting = this.state.submitting;

    for (const slot of this.refs.slots) {
      const idx = Number(slot.dataset.slotIndex || -1);
      const item = items[idx];

      slot.hidden = idx >= tierSize;

      const plus = slot._plus ?? (slot._plus = slot.querySelector('[data-slot-plus]'));
      const content = slot._content ?? (slot._content = slot.querySelector('[data-slot-content]'));
      const image = slot._image ?? (slot._image = slot.querySelector('[data-slot-image]'));
      const removeBtn = slot._removeBtn ?? (slot._removeBtn = slot.querySelector('[data-slot-remove]'));

      if (!plus || !content || !(image instanceof HTMLImageElement)) continue;

      if (item) {
        plus.hidden = true;
        content.hidden = false;
        if (image.src !== item.inventoryImage) {
          image.src = item.inventoryImage;
          image.alt = item.title;
        }
        image.hidden = false;
        slot.style.backgroundColor = item.color;
        slot.classList.add('is-filled');
        if (removeBtn instanceof HTMLButtonElement) {
          removeBtn.hidden = false;
          removeBtn.disabled = submitting;
        }
      } else {
        plus.hidden = false;
        content.hidden = true;
        if (image.src) {
          image.removeAttribute('src');
          image.alt = '';
        }
        image.hidden = true;
        slot.style.backgroundColor = '';
        slot.classList.remove('is-filled');
        if (removeBtn instanceof HTMLButtonElement) {
          removeBtn.hidden = true;
          removeBtn.disabled = true;
        }
      }
    }
  }

  #renderTotals(pricing) {
    const tier = this.#getCurrentTier();
    const count = this.state.selectedItems.length;
    const bundleReady = count === Number(tier.size || 0);
    const isMobile = this.#mobileQuery.matches;
    const suffix = this.config.strings.selectionSuffix;
    const mobileLabel = bundleReady
      ? `${count}/${tier.size} - FINALIZE YOUR ORDER`
      : `${count}/${tier.size} ${suffix}`;

    for (const node of this.refs.tierCounts) {
      node.textContent = String(tier.size);
    }

    if (this.refs.subtotal) this.refs.subtotal.textContent = this.#formatMoney(pricing.subtotal);
    if (this.refs.savings) this.refs.savings.textContent = this.#formatMoney(pricing.savings);
    if (this.refs.total) this.refs.total.textContent = this.#formatMoney(pricing.total);

    if (this.refs.originalTotal) {
      this.refs.originalTotal.hidden = pricing.savings <= 0;
      this.refs.originalTotal.textContent = this.#formatMoney(pricing.subtotal);
    }

    if (this.refs.status) {
      this.refs.status.textContent = `${count}/${tier.size} ${suffix}`;
    }

    if (this.refs.submit) this.refs.submit.disabled = this.state.submitting || !bundleReady;

    if (this.refs.submitLabel) {
      this.refs.submitLabel.textContent = this.state.submitting
        ? this.config.strings.ctaAdding
        : bundleReady
          ? `${this.config.strings.ctaReadyPrefix} - ${this.#formatMoney(pricing.total)}`
          : `${count}/${tier.size} ${suffix}`;
    }

    if (this.refs.submitCompare) {
      const show = !this.state.submitting && bundleReady && pricing.savings > 0;
      this.refs.submitCompare.hidden = !show;
      this.refs.submitCompare.textContent = this.#formatMoney(pricing.subtotal);
    }

    if (this.refs.mobileToggleLabel) {
      this.refs.mobileToggleLabel.textContent = mobileLabel;
    }

    if (this.refs.mobileToggle instanceof HTMLButtonElement) {
      this.refs.mobileToggle.classList.toggle('is-ready', bundleReady);
      this.refs.mobileToggle.setAttribute(
        'aria-expanded',
        String(isMobile ? this.state.mobileSummaryExpanded : true)
      );
    }

    if (this.refs.summaryBody instanceof HTMLElement) {
      this.refs.summaryBody.hidden = isMobile ? !this.state.mobileSummaryExpanded : false;
    }
  }

  #render() {
    this.#renderTabs();
    this.#applyFilters();
    this.#renderCards();
    this.#renderTiers();

    const commonPlans = this.#getCommonSellingPlans();
    const pricing = this.#getPricing(commonPlans);
    this.#renderPurchaseMode(commonPlans, pricing);
    this.#renderTotals(pricing);

    this.#renderSlots();
    syncOpenBundleBuilderDrawerControls();
  }

  #formatMoney(value) {
    return formatMoney(value, this.config.moneyFormat, this.config.currency);
  }

  #clearError() {
    if (!this.refs.error) return;
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  }

  #showError(message) {
    if (!this.refs.error) return;
    this.refs.error.hidden = false;
    this.refs.error.textContent = message || this.config.strings.errorFallback;
  }

  #getRechargeConfig() {
    const r = this.config?.recharge ?? {};
    return {
      bundleProductId: String(r.bundleProductId || '').trim(),
      bundleVariantId: String(r.bundleVariantId || '').trim(),
      bundleHandle: String(r.bundleHandle || '').trim(),
      bundleCollectionId: String(r.bundleCollectionId || '').trim(),
    };
  }

  #hasRechargeConfig() {
    const r = this.#getRechargeConfig();
    return Boolean(r.bundleProductId && r.bundleVariantId && r.bundleHandle && r.bundleCollectionId);
  }

  #generateRechargeBundleToken(length = 9) {
    const buf = new Uint8Array(length);
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(buf);
    } else {
      for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 62);
    }
    return [...buf].map((v) => this.#bundleTokenAlphabet[v % 62]).join('');
  }

  async #submitBundle() {
    const tier = this.#getCurrentTier();
    if (this.state.submitting || this.state.selectedItems.length !== Number(tier.size || 0)) return;

    if (!this.#hasRechargeConfig()) {
      this.#showError(
        'Recharge bundle settings are missing. Add the bundle handle, product ID, child collection ID, and variant ID in the section settings.'
      );
      return;
    }

    this.state.submitting = true;
    this.#clearError();
    this.#scheduleRender();

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    const recharge = this.#getRechargeConfig();
    const bundleGroup = `${this.#generateRechargeBundleToken()}:${recharge.bundleProductId}`;
    const commonPlans = this.#getCommonSellingPlans();
    const activePlan =
      this.state.purchaseMode === 'subscribe' ? this.#getActiveSellingPlanOption(commonPlans) : null;

    const payload = {
      items: this.state.selectedItems.map((item, i) => {
        const planId =
          this.state.purchaseMode === 'subscribe'
            ? Number(activePlan?.itemPlans?.[i]?.id || 0) : 0;
        return {
          id: Number(item.variantId),
          quantity: 1,
          ...(planId ? { selling_plan: planId } : {}),
          properties: {
            _rc_bundle: bundleGroup,
            _rc_bundle_parent: recharge.bundleHandle,
            _rc_bundle_variant: recharge.bundleVariantId,
            _rc_bundle_collection_id: recharge.bundleCollectionId,
          },
        };
      }),
      sections,
      sections_url: window.location.pathname,
    };

    try {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const addResult = await addRes.json();

      if (!addRes.ok || addResult.status) {
        const msg = addResult.description || addResult.message || this.config.strings.errorFallback;
        this.dispatchEvent(new CartErrorEvent(this.id, msg, addResult.description, addResult.errors));
        this.#showError(msg);
        return;
      }

      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();

      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'product-form-component',
          itemCount: this.state.selectedItems.length,
          sections: addResult.sections,
        })
      );

      this.state.selectedItems = [];
      this.state.tierKey = this.#getDefaultTierKey();
      this.state.purchaseMode = 'one-time';
      this.state.activeFilters = [];
      for (const t of this.refs.filterToggles) {
        if (t instanceof HTMLInputElement) t.checked = false;
      }
    } catch (err) {
      console.error(err);
      this.#showError(this.config.strings.errorFallback);
    } finally {
      this.state.submitting = false;
      this.#scheduleRender();
    }
  }
  
}

if (!customElements.get('bundle-builder-component')) {
  customElements.define('bundle-builder-component', BundleBuilderComponent);
}


/* PDP Drawer */
function closeBundleBuilderPdpDrawer() {
  document.body.classList.remove('bundle_builder_pdp_drawer_is_open');

  document.querySelectorAll('.bundle_builder_pdp_popup_new.open')
    .forEach(el => {
      el.classList.remove('open');
      el.setAttribute('aria-hidden', 'true');
    });
}

function getBundleBuilderDrawerCard(productId) {
  const component = [...document.querySelectorAll('bundle-builder-component')]
    .find(item => typeof item.getDrawerProductCard === 'function' && item.getDrawerProductCard(productId));

  return component?.getDrawerProductCard(productId) || null;
}

function syncBundleBuilderDrawerControls(drawer, productId) {
  if (!(drawer instanceof HTMLElement) || !productId) return;

  const card = getBundleBuilderDrawerCard(productId);
  const cardAddButton = card?.querySelector('[data-product-toggle]');
  const cardAddButtonText = cardAddButton?.querySelector('[data-card-button-text]');
  const cardQty = card?.querySelector('[data-card-quantity]');
  const cardQtyCount = card?.querySelector('[data-qty-count]');
  const cardIncrement = card?.querySelector('[data-qty-increment]');
  const cardDecrement = card?.querySelector('[data-qty-decrement]');
  const drawerAddButton = drawer.querySelector('[data-pdp-drawer-add]');
  const drawerQty = drawer.querySelector('[data-pdp-drawer-quantity]');
  const drawerQtyCount = drawer.querySelector('[data-pdp-drawer-qty-count]');
  const drawerIncrement = drawer.querySelector('[data-pdp-drawer-qty-increment]');
  const drawerDecrement = drawer.querySelector('[data-pdp-drawer-qty-decrement]');

  if (drawerAddButton instanceof HTMLButtonElement) {
    drawerAddButton.disabled = !(cardAddButton instanceof HTMLButtonElement) || cardAddButton.disabled || cardAddButton.hidden;
    drawerAddButton.hidden = cardAddButton instanceof HTMLButtonElement ? cardAddButton.hidden : false;
    drawerAddButton.textContent = cardAddButtonText?.textContent?.trim() || 'add to bundle';
  }

  if (drawerQty instanceof HTMLElement) drawerQty.hidden = !(cardQty instanceof HTMLElement) || cardQty.hidden;
  if (drawerQtyCount) drawerQtyCount.textContent = cardQtyCount?.textContent || '0';
  if (drawerIncrement instanceof HTMLButtonElement) {
    drawerIncrement.disabled = !(cardIncrement instanceof HTMLButtonElement) || cardIncrement.disabled;
  }
  if (drawerDecrement instanceof HTMLButtonElement) {
    drawerDecrement.disabled = !(cardDecrement instanceof HTMLButtonElement) || cardDecrement.disabled;
  }
}

function syncOpenBundleBuilderDrawerControls() {
  const drawer = document.querySelector('.bundle_builder_pdp_popup_new.bundle_builder_pdp_popup.open');
  const productId = drawer?.querySelector('[data-pdp-drawer-add]')?.dataset.productId;
  syncBundleBuilderDrawerControls(drawer, productId);
}

initMarquee('.Product_benefits_marquees_marquee', {
    mobile: {
        duration: 50000,
        gap: 0,
        duplicated: true,
        direction: "left"
    },
    desktop: {
        duration: 80000,
        gap: 0,
        duplicated: true,
        direction: "left"
    }
});

document.addEventListener('click', function (e) {
  const drawerAddButton = e.target.closest('[data-pdp-drawer-add]');
  if (drawerAddButton) {
    const productId = drawerAddButton.dataset.productId;
    [...document.querySelectorAll('bundle-builder-component')]
      .some(component => typeof component.addProductFromDrawer === 'function' && component.addProductFromDrawer(productId));
    requestAnimationFrame(syncOpenBundleBuilderDrawerControls);
    return;
  }

  const drawerQtyButton = e.target.closest('[data-pdp-drawer-qty-increment], [data-pdp-drawer-qty-decrement]');
  if (drawerQtyButton) {
    const drawer = drawerQtyButton.closest('.bundle_builder_pdp_popup_new');
    const productId = drawer?.querySelector('[data-pdp-drawer-add]')?.dataset.productId;
    const action = drawerQtyButton.hasAttribute('data-pdp-drawer-qty-increment') ? 'increment' : 'decrement';

    [...document.querySelectorAll('bundle-builder-component')]
      .some(component => (
        typeof component.clickDrawerProductQuantity === 'function' &&
        component.clickDrawerProductQuantity(productId, action)
      ));
    requestAnimationFrame(syncOpenBundleBuilderDrawerControls);
    return;
  }

  const trigger = e.target.closest('.bundle_builder_pdp_drawer_open');
  if (!trigger) return;

  const card = trigger.closest('[data-product-card]');
  const dataNode = card?.querySelector('[data-pdp-drawer-data]');
  const target = document.querySelector('.bundle_builder_pdp_popup_new.bundle_builder_pdp_popup');
  if (!dataNode || !target) return;

  let drawerData;
  try {
    drawerData = JSON.parse(dataNode.textContent || '{}');
  } catch {
    return;
  }

  // close any open popup first
  document.querySelectorAll('.bundle_builder_pdp_popup_new.open')
    .forEach(el => el.classList.remove('open'));

  const title = target.querySelector('[data-pdp-drawer-title]');
  const price = target.querySelector('[data-pdp-drawer-price]');
  const box = target.querySelector('[data-pdp-drawer-box]');
  const content = target.querySelector('[data-pdp-drawer-content]');
  const addButton = target.querySelector('[data-pdp-drawer-add]');

  if (title) title.textContent = drawerData.title || '';
  if (price) price.innerHTML = drawerData.priceHtml || '';
  if (box) box.innerHTML = drawerData.boxHtml || '';
  if (content) content.innerHTML = drawerData.contentHtml || '';
  if (addButton) {
    addButton.dataset.productId = drawerData.productId || '';
    syncBundleBuilderDrawerControls(target, drawerData.productId);
  }
  if (typeof window.initCustomAccordion === 'function') {
    window.initCustomAccordion(target);
  }
  if (typeof initMarquee === 'function') {
    initMarquee('.Product_benefits_marquees_marquee', {
      mobile: {
        duration: 50000,
        gap: 0,
        duplicated: true,
        direction: "left"
      },
      desktop: {
        duration: 80000,
        gap: 0,
        duplicated: true,
        direction: "left"
      }
    });
  }

  target.classList.add('open');
  target.setAttribute('aria-hidden', 'false');
  document.body.classList.add('bundle_builder_pdp_drawer_is_open');
});

document.addEventListener('click', function (e) {
  const closeTrigger = e.target.closest('.bundle_builder_pdp_drawer_overlay_new, .bundle_builder_pdp_drawer_close');
  if (!closeTrigger) return;

  closeBundleBuilderPdpDrawer();
});
/* PDP Drawer */
